import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import isUndefined from 'lodash/isUndefined'

import { categoryNamePlaceholder, tabsWithChildElements, TaskOptions } from '../../components/admin/constants'
import {
    AdminMovingElement,
    LastUpdatedTime,
    MTPReference,
    PreparedAdminOption,
    PreparedCategoryAdminOption,
    PreparedErrorAdminOption,
    PreparedSubCategoryAdminOption,
    PreparedSubCategoryChildrenOption,
    ResponseAdminOptions,
} from '../../models/adminOptions'
import { RootState } from '../../stores'

export type StateAdmin = {
    activeTab: TaskOptions
    intakeMethods: PreparedAdminOption[]
    statuses: PreparedAdminOption[]
    severities: PreparedAdminOption[]
    categories: PreparedCategoryAdminOption[]
    subCategories: PreparedSubCategoryAdminOption[]
    requirementSources: PreparedAdminOption[]
    errors: PreparedErrorAdminOption[]
    lastUpdatedTime: LastUpdatedTime
    isPublishButtonDisabled: boolean
    publishStatus: {
        isLoading: boolean
        isSuccess: boolean
        isError: boolean
        errorMessage?: string
    }
}

export const initialAdminState: StateAdmin = {
    activeTab: TaskOptions.STATUSES,
    intakeMethods: [],
    statuses: [],
    severities: [],
    categories: [],
    subCategories: [],
    requirementSources: [],
    errors: [],
    lastUpdatedTime: {
        statuses: '',
        severities: '',
        categories: '',
        subCategories: '',
        intakeMethods: '',
        requirementSources: '',
        errors: '',
    },
    isPublishButtonDisabled: true,
    publishStatus: {
        isLoading: false,
        isSuccess: false,
        isError: false,
        errorMessage: '',
    },
}

const sortByOrder = (a, b) => {
    return a.order - b.order
}

/**
 * Function check for id or fakeId
 * id - is used when option is added and saved, after that we have id from database, and we can move/delete by id
 * fakeId - is used when option is added and wasn't saved, we need it to be able to move/delete it without using option name.
 *
 * Option name can be the same.
 *
 * @param currentOption
 * @param optionId
 * @param optionFakeId
 */
const checkByIdOrFakeId = (
    currentOption: PreparedAdminOption,
    optionId: number | undefined,
    optionFakeId: string
): boolean => {
    if (!isUndefined(optionId)) {
        return currentOption.id === optionId
    }

    return currentOption.fakeId === optionFakeId
}

const handleSetOptions = (state: StateAdmin, { payload }: PayloadAction<{ options: ResponseAdminOptions }>) => {
    const {
        intakeMethods = { items: [], lastChangeTime: '' },
        statuses = { items: [], lastChangeTime: '' },
        severities = { items: [], lastChangeTime: '' },
        categories = { items: [], lastChangeTime: '' },
        subCategories = { items: [], lastChangeTime: '' },
        requirementSources = { items: [], lastChangeTime: '' },
        errors = { items: [], lastChangeTime: '' },
    } = payload.options

    state.intakeMethods = intakeMethods.items.map((item) => ({ ...item, isActive: true }))
    state.statuses = statuses.items
    state.severities = severities.items
    state.categories = categories.items
    state.subCategories = categories.items.map((category) => ({
        ...category,
        children: subCategories.items.filter((subcategory) => subcategory.category_id === category.id),
    }))
    state.requirementSources = requirementSources.items
    state.errors = categories.items.map((category) => ({
        ...category,
        children: errors.items.filter((error) => error.category_id === category.id),
    }))

    state.lastUpdatedTime = {
        intakeMethods: statuses.lastChangeTime,
        statuses: statuses.lastChangeTime,
        severities: severities.lastChangeTime,
        categories: categories.lastChangeTime,
        subCategories: subCategories.lastChangeTime,
        requirementSources: requirementSources.lastChangeTime,
        errors: errors.lastChangeTime,
    }
}

const handleSetIsActiveOption = (state: StateAdmin, { payload }: PayloadAction<any>) => {
    state.isPublishButtonDisabled = false

    if (state.activeTab === TaskOptions.SUBCATEGORIES || state.activeTab === TaskOptions.ERRORS) {
        state[state.activeTab] = state[state.activeTab].map((item) => {
            return {
                ...item,
                children: item.children.map((children) => {
                    return children.id === payload.id && children.category_id === payload.category_id
                        ? { ...children, is_active: !children?.is_active }
                        : children
                }),
            }
        })

        return
    }

    state[state.activeTab] = state[state.activeTab].map((item) =>
        item.id === payload.id ? { ...item, is_active: !item.is_active } : item
    )
}

const handleSetNewOption = (
    state: StateAdmin,
    {
        payload,
    }: PayloadAction<{
        optionValue: string
        fakeId: string
        isCopied?: boolean
        isNew?: boolean
        categoryId?: number
        is_active?: boolean
    }>
) => {
    const { optionValue, fakeId, categoryId, isCopied, isNew, is_active = true } = payload

    if (state.activeTab === TaskOptions.ERRORS) {
        if (categoryId) {
            state.errors = state.errors.map((error) => {
                if (error.id === categoryId) {
                    return {
                        ...error,
                        children: [
                            ...error.children,
                            {
                                name: optionValue,
                                category_id: categoryId,
                                isCopied: isCopied,
                                isNew: isNew,
                                order: error.children.length + 1,
                                fakeId,
                                is_active,
                            },
                        ],
                    }
                }

                return error
            })

            state.isPublishButtonDisabled = false

            return
        }

        const newErrorPlaceholder = state.errors.find((error) => error.name === categoryNamePlaceholder)

        if (newErrorPlaceholder) {
            state.errors = state.errors.map((error) => {
                if (error.name === newErrorPlaceholder.name) {
                    return {
                        ...error,
                        children: [
                            { name: optionValue, category_id: -1, isNew: true, is_active, order: 1, fakeId },
                            ...error.children,
                        ].map((option, index) => ({ ...option, order: index + 1 })),
                    }
                }

                return error
            })
        } else {
            state.errors = [
                {
                    name: categoryNamePlaceholder,
                    order: 1,
                    children: [{ name: optionValue, isNew: true, is_active, category_id: -1, order: 1, fakeId }],
                    id: -1,
                    is_active,
                },
                ...state.errors,
            ].map((option, index) => ({ ...option, order: index + 1 }))
        }

        return
    }

    if (state.activeTab === TaskOptions.SUBCATEGORIES) {
        if (categoryId) {
            state.subCategories = state.subCategories.map((subCategory) => {
                if (subCategory.id === categoryId) {
                    return {
                        ...subCategory,
                        children: [
                            ...subCategory.children,
                            {
                                name: optionValue,
                                category_id: categoryId,
                                isCopied: isCopied,
                                isNew: isNew,
                                order: subCategory.children.length + 1,
                                mtp_references: [],
                                fakeId,
                                is_active,
                            },
                        ],
                    }
                }

                return subCategory
            })
            state.isPublishButtonDisabled = false

            return
        }

        const newCategoryPlaceholder = state.subCategories.find((category) => category.name === categoryNamePlaceholder)

        if (newCategoryPlaceholder) {
            // add subcategory into "New Categories"
            state.subCategories = state.subCategories.map((subCategory) => {
                if (subCategory.name === newCategoryPlaceholder.name) {
                    return {
                        ...subCategory,
                        children: [
                            {
                                name: optionValue,
                                category_id: -1,
                                isNew: true,
                                is_active,
                                order: 1,
                                mtp_references: [],
                                fakeId,
                            },
                            ...subCategory.children,
                        ].map((option, index) => ({ ...option, order: index + 1 })),
                    }
                }

                return subCategory
            })
        } else {
            // create "New Categories" temporary category and add first subcategory
            state.subCategories = [
                {
                    name: categoryNamePlaceholder,
                    order: 1,
                    children: [
                        {
                            name: optionValue,
                            isNew: true,
                            is_active,
                            category_id: -1,
                            order: 1,
                            mtp_references: [],
                            fakeId,
                        },
                    ],
                    mtp_references: [],
                    id: -1,
                    is_active,
                },
                ...state.subCategories,
            ].map((option, index) => ({ ...option, order: index + 1 }))
        }

        return
    }

    if (state.activeTab === TaskOptions.CATEGORIES) {
        state.categories = [
            { name: optionValue, isNew: true, is_active, order: 1, mtp_references: [], fakeId },
            ...state.categories,
        ].map((option, index) => ({ ...option, order: index + 1 }))

        state.subCategories = [
            { name: optionValue, isNew: true, is_active, children: [], order: 1, mtp_references: [], fakeId },
            ...state.subCategories,
        ].map((option, index) => ({ ...option, order: index + 1 }))
        state.isPublishButtonDisabled = false

        return
    }

    // rest of task options
    state[state.activeTab] = [
        { name: optionValue, isNew: true, is_active, order: 1, fakeId },
        ...state[state.activeTab],
    ].map((option, index) => ({ ...option, order: index + 1 })) as PreparedAdminOption[]

    state.isPublishButtonDisabled = false
}

const handleResetNewOptions = (state: StateAdmin, { payload }: PayloadAction<{ optionName: string }>) => {
    const { optionName } = payload

    // when a category is removed, remove it from subcategory and remove mpt references
    if (state.activeTab === TaskOptions.CATEGORIES) {
        // remove mtp_references from categories when mtp_reference is new and we reset
        state.categories = state.categories.map((category) => {
            return {
                ...category,
                mtp_references: category.mtp_references.filter((mtpRef) => !mtpRef.isNew),
            }
        })

        // remove mtp_references from subcategories when mtp_reference is new and we reset
        state.subCategories = state.subCategories
            .map((subCategory) => {
                return {
                    ...subCategory,
                    mtp_references: subCategory.mtp_references.filter((mtpRef) => !mtpRef.isNew),
                }
            })
            .filter((subCategory) => !subCategory.isNew)
    }

    if (state.activeTab === TaskOptions.ERRORS) {
        state[state.activeTab] = state[state.activeTab].map((item) => {
            return {
                ...item,
                children: item.children
                    .map((option) => {
                        return {
                            ...option,
                            isSoftDeleted: false,
                        }
                    })
                    .filter((option) => !option.isNew),
            }
        })

        return
    }

    if (state.activeTab === TaskOptions.SUBCATEGORIES) {
        state.subCategories = state.subCategories.map((subcategory) => {
            return {
                ...subcategory,
                children: subcategory.children
                    .map((option) => {
                        return {
                            ...option,
                            isSoftDeleted: false,
                            mtp_references: option.mtp_references.filter((mtpRef) => !mtpRef.isNew),
                        }
                    })
                    .filter((option) => !option.isNew),
            }
        })

        return
    }

    state[optionName] = (state[optionName] as PreparedAdminOption[])
        .map((option) => ({ ...option, isSoftDeleted: false })) // display deleted items
        .filter((option) => !option.isNew) // remove new options
        .sort(sortByOrder)
}

const handleSoftDeleteOption = (
    state: StateAdmin,
    { payload }: PayloadAction<{ optionId: number; optionFakeId: string }>
) => {
    const { optionId, optionFakeId } = payload

    if (state.activeTab === TaskOptions.SUBCATEGORIES || state.activeTab === TaskOptions.ERRORS) {
        // remove a new option or mark isSoftDeleted if we can restore it
        state[state.activeTab] = state[state.activeTab]
            .map((item) => {
                return {
                    ...item,
                    children: item.children
                        // mark option isSoftDeleted to hide it on FE
                        .map((option) => {
                            return checkByIdOrFakeId(option, optionId, optionFakeId)
                                ? { ...option, isSoftDeleted: true }
                                : option
                        })
                        // filter option if options are new and were deleted, we don't need keep it
                        .filter((option) => {
                            const isSameIdOrFakeId = checkByIdOrFakeId(option, optionId, optionFakeId)

                            return !(isSameIdOrFakeId && option.isNew && option.isSoftDeleted)
                        }),
                }
            })
            .filter((item) => !(item.name === categoryNamePlaceholder && item.children.length === 0))
        state.isPublishButtonDisabled = false

        return
    }

    // when we delete the category, remove the category in subcategories
    if (state.activeTab === TaskOptions.CATEGORIES) {
        state.subCategories = state.subCategories.map((subcategory) => {
            const isSameIdOrFakeId = checkByIdOrFakeId(subcategory, optionId, optionFakeId)

            if (isSameIdOrFakeId) {
                return {
                    ...subcategory,
                    isSoftDeleted: true,
                }
            }

            return subcategory
        })
    }

    state[state.activeTab] = state[state.activeTab]
        .map((option) => {
            const isSameIdOrFakeId = checkByIdOrFakeId(option, optionId, optionFakeId)

            return isSameIdOrFakeId ? { ...option, isSoftDeleted: true } : option
        })
        .filter((option) => {
            const isSameIdOrFakeId = checkByIdOrFakeId(option, optionId, optionFakeId)

            return !(isSameIdOrFakeId && option.isNew && option.isSoftDeleted)
        })

    state.isPublishButtonDisabled = false
}

const handleSetActiveTab = (state: StateAdmin, { payload }: PayloadAction<{ activeTab: TaskOptions }>) => {
    state.activeTab = payload.activeTab
}

const handleNewOrder = (
    state: StateAdmin,
    {
        payload,
    }: PayloadAction<{
        dragIndex: number
        hoverIndex: number
        movingElement: AdminMovingElement
        moveToGroup: string
    }>
) => {
    const { dragIndex, hoverIndex, movingElement, moveToGroup } = payload

    const isMovingInsideSameGroup = movingElement.groupName === moveToGroup

    if (state.activeTab === TaskOptions.CATEGORIES) {
        const updatedCategories = [...state.categories]
        const [movedItem] = updatedCategories.splice(dragIndex, 1)

        updatedCategories.splice(hoverIndex, 0, movedItem)
        state.categories = updatedCategories

        return
    }

    if (state.activeTab === TaskOptions.SUBCATEGORIES || state.activeTab === TaskOptions.ERRORS) {
        state[state.activeTab] = state[state.activeTab].map((item) => {
            // move inside the same group, change the order of elements
            if (isMovingInsideSameGroup && item.name === movingElement.groupName) {
                const updatedChildren = [...item.children]
                const [movedChild] = updatedChildren.splice(dragIndex, 1)

                updatedChildren.splice(hoverIndex, 0, movedChild)

                return {
                    ...item,
                    children: updatedChildren,
                }
            }

            return item
        })

        return
    }

    // rest of task options
    const updatedTab = [...state[state.activeTab]]
    const [movedItem] = updatedTab.splice(dragIndex, 1)

    updatedTab.splice(hoverIndex, 0, movedItem)
    state[state.activeTab] = updatedTab
    state.isPublishButtonDisabled = false
}

const handleMoveBetween = (
    state: StateAdmin,
    {
        payload,
    }: PayloadAction<{
        movingElement: AdminMovingElement
        moveTo: string
    }>
) => {
    const {
        movingElement: { id, name, isNew, category_id, groupName, fakeId, is_active },
        moveTo,
    } = payload

    const elementToAdd = {
        id,
        fakeId,
        name,
        isNew,
        is_active,
        category_id,
        mtp_references: [],
    }

    state[state.activeTab] = state[state.activeTab]
        .map((item, index) => {
            // on the move, remove subcategory from an initial group
            if (item.name === groupName) {
                return {
                    ...item,
                    order: index + 1,
                    children: item.children.filter((child) => {
                        if (child.fakeId) {
                            return child.fakeId !== fakeId
                        }

                        return child.id !== id
                    }),
                }
            }

            // on the move, move subcategory to a new group
            if (item.name === moveTo) {
                return {
                    ...item,
                    order: index + 1,
                    children: [...item.children, elementToAdd].map((option, index) => ({
                        ...option,
                        category_id: item.id,
                        order: index + 1,
                    })),
                }
            }

            return { ...item, order: index + 1 }
        })
        .filter((item) => !(item.name === categoryNamePlaceholder && !item.children.length))

    state.isPublishButtonDisabled = false
}

const handleAddMptReference = (
    state: StateAdmin,
    {
        payload,
    }: PayloadAction<{
        item: PreparedCategoryAdminOption | PreparedSubCategoryChildrenOption
        mtpReferenceName: string
    }>
) => {
    if (state.activeTab === TaskOptions.SUBCATEGORIES) {
        state.subCategories = state.subCategories.map((option) => {
            if (option.id === (payload.item as PreparedSubCategoryChildrenOption).category_id) {
                return {
                    ...option,
                    children: option.children.map((child) => {
                        if (child.id === payload.item.id) {
                            return {
                                ...child,
                                mtp_references: child.mtp_references
                                    ? [{ name: payload.mtpReferenceName, isNew: true }, ...child.mtp_references]
                                    : [{ name: payload.mtpReferenceName, isNew: true }],
                            }
                        }

                        return child
                    }),
                }
            }

            return option
        })
    }

    if (state.activeTab === TaskOptions.CATEGORIES) {
        state.categories = state.categories.map((option) => {
            if (option.id === payload.item.id) {
                return {
                    ...option,
                    mtp_references: option?.mtp_references
                        ? [{ name: payload.mtpReferenceName, isNew: true }, ...option.mtp_references]
                        : [{ name: payload.mtpReferenceName, isNew: true }],
                }
            }

            return option
        })
    }

    state.isPublishButtonDisabled = false
}

const getUpdateMTPReferenceWithSoftDeleted = (
    mtp_references: MTPReference[],
    mtpReference: MTPReference
): MTPReference[] => {
    return mtp_references
        .map((reference) => {
            if ((mtpReference?.id && mtpReference.id === reference.id) || mtpReference.name === reference.name) {
                return {
                    ...reference,
                    isSoftDeleted: true,
                }
            }

            return reference
        })
        .filter((reference) => {
            return !(reference.name === mtpReference.name && reference.isNew && reference.isSoftDeleted)
        })
}

const handleSoftDeleteMtpReference = (
    state: StateAdmin,
    {
        payload,
    }: PayloadAction<{
        item: PreparedCategoryAdminOption | PreparedSubCategoryChildrenOption
        mtpReference: MTPReference
    }>
) => {
    if (state.activeTab === TaskOptions.CATEGORIES) {
        state.categories = state.categories.map((option) => {
            if (option.id === payload.item.id) {
                return {
                    ...option,
                    mtp_references: getUpdateMTPReferenceWithSoftDeleted(option.mtp_references, payload.mtpReference),
                }
            }
            state.isPublishButtonDisabled = false

            return option
        })
    }

    if (state.activeTab === TaskOptions.SUBCATEGORIES) {
        state.subCategories = state.subCategories.map((option) => {
            const categoryId = (payload.item as PreparedSubCategoryChildrenOption).category_id

            if (option.id === categoryId) {
                return {
                    ...option,
                    children: option.children.map((childrenOption) => {
                        if (childrenOption.id === payload.item.id) {
                            return {
                                ...childrenOption,
                                mtp_references: getUpdateMTPReferenceWithSoftDeleted(
                                    childrenOption.mtp_references,
                                    payload.mtpReference
                                ),
                            }
                        }

                        return childrenOption
                    }),
                }
            }
            state.isPublishButtonDisabled = false

            return option
        })
    }
}

const handleResetPublishButtonDisabled = (state: StateAdmin) => {
    state.isPublishButtonDisabled = true
}

const handlePublishStart = (state: StateAdmin) => {
    state.publishStatus = {
        isLoading: true,
        isSuccess: false,
        isError: false,
    }
}

const handlePublishSuccess = (state: StateAdmin) => {
    state.publishStatus = {
        isLoading: false,
        isSuccess: true,
        isError: false,
    }
}

const handlePublishError = (state: StateAdmin, { payload }: PayloadAction<string>) => {
    state.publishStatus = {
        isLoading: false,
        isSuccess: false,
        isError: true,
        errorMessage: payload,
    }
}

const reducers = {
    setIsActiveOption: handleSetIsActiveOption,
    setActiveTab: handleSetActiveTab,
    setOptions: handleSetOptions,
    setNewOption: handleSetNewOption,
    softDeleteOption: handleSoftDeleteOption,
    resetNewOptions: handleResetNewOptions,
    setNewOrder: handleNewOrder,
    moveBetween: handleMoveBetween,
    addMtpReference: handleAddMptReference,
    softDeleteMtpReference: handleSoftDeleteMtpReference,
    setPublishButtonDisabled: handleResetPublishButtonDisabled,
    publishStart: handlePublishStart,
    publishSuccess: handlePublishSuccess,
    publishError: handlePublishError,
}

const adminSlice = createSlice({
    name: 'admin',
    initialState: initialAdminState,
    reducers,
})

export const {
    setIsActiveOption,
    setActiveTab,
    setOptions,
    setNewOption,
    softDeleteOption,
    resetNewOptions,
    setNewOrder,
    moveBetween,
    addMtpReference,
    softDeleteMtpReference,
    setPublishButtonDisabled,
    publishStart,
    publishSuccess,
    publishError,
} = adminSlice.actions

export const selectActiveTab = createSelector(
    (state: RootState) => state.IMUP.admin.activeTab,
    (activeTab) => activeTab
)

export const selectLastUpdated = createSelector(
    (state: RootState) => state.IMUP.admin.lastUpdatedTime,
    (lastUpdatedTime) => lastUpdatedTime
)

export const selectOptions = createSelector(
    (state: RootState) => {
        return {
            statuses: state.IMUP.admin.statuses,
            severities: state.IMUP.admin.severities,
            categories: state.IMUP.admin.categories,
            subCategories: state.IMUP.admin.subCategories,
            intakeMethods: state.IMUP.admin.intakeMethods,
            requirementSources: state.IMUP.admin.requirementSources,
            errors: state.IMUP.admin.errors,
        }
    },
    (options) => options
)

export const selectPublishButtonDisabled = createSelector(
    (state: RootState) => state.IMUP.admin.isPublishButtonDisabled,
    (isPublishButtonDisabled) => isPublishButtonDisabled
)

export const selectIsNewCategoryExist = createSelector([selectOptions, selectActiveTab], (options, activeTab) => {
    if (tabsWithChildElements.includes(activeTab)) {
        return !!options.categories.find((category) => category?.isNew)
    }
})

export const selectFinalPublishButtonDisabled = createSelector(
    [selectPublishButtonDisabled, selectOptions, selectActiveTab],
    (stateDisabled, options, activeTab) => {
        if (activeTab === TaskOptions.SUBCATEGORIES) {
            const hasNewCategory = !!options.categories.find((category) => category?.isNew)

            const hasUnassignedElements = options.subCategories.some((subcategory) => subcategory.name === 'Unassigned')
            const hasChildrenWithFakeId = options.subCategories.some((subcategory) =>
                subcategory.children.some((child) => child.fakeId)
            )

            if (hasNewCategory) {
                return true
            }

            if (hasUnassignedElements && !hasNewCategory) {
                return true
            }
            if (hasChildrenWithFakeId && !hasNewCategory) {
                return false
            }
        }

        if (stateDisabled === false) return false

        const hasNewItemsInList = options[activeTab]?.some((item) => item?.isNew)

        return !hasNewItemsInList
    }
)

export const selectPublishStatus = createSelector(
    (state: RootState) => state.IMUP.admin.publishStatus,
    (publishStatus) => publishStatus
)
export default adminSlice
